import User from "@/lib/user"
import { CoursesAPI, UsersAPI } from "@/lib/api"
import {
  ADD_COURSE,
  ADD_LEARNING_ITEM,
  ASSIGN_LEARNING_ITEMS,
  ASSIGN_COURSES,
  DELETE_COURSE,
  SET_COURSES,
  SET_COURSE,
  UPDATE_COURSE
} from "@/store/mutationTypes"
const state = {
  all: []
}

// getters
const getters = {
  getCourses: (state, getters, rootState, rootGetters) => {
    const learningItems = rootGetters["learningItems/getLearningItems"]
    let result = state.all.map(course => {
      let mappedItems = course.learning_items.map(learningItemId =>
        learningItems.find(
          learningItemFromStore => learningItemFromStore.id === learningItemId
        )
      )
      return Object.assign({}, course, {
        learning_items: mappedItems.filter(el => el)
      })
    })
    return result
  },
  getCompletedCourses: (state, getters) =>
    getters.getCourses.filter(
      el => el.learning_items.every(el => el.status) && el.touched
    ),
  getIncompleteCourses: (state, getters) =>
    getters.getCourses.filter(
      el => el.learning_items.some(el => !el.status) || !el.touched
    ),
  getTemplates: (state, getters) =>
    getters.getCourses.filter(el => el.template),
  getCourseById: (state, getters) => id => {
    return getters.getCourses.find(el => el.id === parseInt(id))
  },
  getCoursesByUserId: (state, getters) => id => {
    return getters.getCourses.filter(
      el => el.user_id === parseInt(id) && !el.hidden
    )
  },
  getCompletedCoursesByUserId: (state, getters) => id => {
    let courses = getters.getCourses.filter(
      el =>
        el.user_id === parseInt(id) &&
        el.learning_items.every(el => el.status) &&
        el.touched
    )

    return courses
  },
  getIncompleteCoursesByUserId: (state, getters) => id => {
    let courses = getters.getCourses.filter(
      el =>
        el.user_id === parseInt(id) &&
        (el.learning_items.some(el => !el.status) || !el.touched)
    )

    return courses
  },
  getCourseByLearningItemId: (state, getters) => id => {
    let c = getters.getCourses.filter(course => {
      let found = course.learning_items.filter(learning_item => {
        return learning_item.id == parseInt(id)
      })

      return found.length > 0
    })

    return c[0] ? c[0] : 0
  },
  getCoursesByParentId: state => id => {
    return state.all.filter(el => el.parent_id === id)
  }
}

// actions
const actions = {
  async loadMemberCourses({ dispatch, commit }, payload) {
    let options = { showTemplates: false, includeHidden: false }
    if (payload.userId) {
      options.userId = payload.userId
    }
    CoursesAPI.list(options).then(courses => {
      const learning_items = courses.map(el => el.learning_items)
      commit("learningItems/SET_LEARNING_ITEMS", learning_items.flat(), {
        root: true
      })
      courses.forEach(course => {
        course.learning_items = course.learning_items.map(el => {
          return el.id
        })
      })
      commit(SET_COURSES, courses)
    })
  },
  async loadCourses({ dispatch, commit }, payload) {
    Promise.all([
      CoursesAPI.list({ showTemplates: false, includeHidden: true }),
      CoursesAPI.list({ showTemplates: true, includeHidden: true })
    ]).then(responses => {
      let courses = responses.flat()
      const learning_items = courses.map(el => el.learning_items)
      commit("learningItems/SET_LEARNING_ITEMS", learning_items.flat(), {
        root: true
      })
      courses.forEach(course => {
        course.learning_items = course.learning_items.map(el => {
          return el.id
        })
      })
      commit(SET_COURSES, courses)
    })
  },
  async loadTemplates({ dispatch, commit }) {
    CoursesAPI.list({ showTemplates: true, includeHidden: false }).then(
      response => {
        let templates = response
        commit(SET_COURSES, templates)
      }
    )
  },
  async loadCourse({ commit, dispatch }, { id, showTemplates, includeHidden }) {
    await dispatch(
      "learningItems/loadLearningItems",
      { showTemplates, includeHidden },
      { root: true }
    )
    let course = await CoursesAPI.get(id, { includeHidden })
    course.learning_items = course.learning_items.map(el => {
      return el.id
    })
    commit(SET_COURSE, course)
  },
  async addCourse(
    { commit, dispatch },
    { badgeId, name, description, template, touched }
  ) {
    commit(
      ADD_COURSE,
      await CoursesAPI.create({
        badgeId,
        name,
        description,
        template,
        touched
      })
    )
    await dispatch("loadCourses")
  },
  async updateCourse(
    { commit, dispatch },
    { id, name, description, badgeId, touched, folder }
  ) {
    commit(
      UPDATE_COURSE,
      await CoursesAPI.update(id, {
        name,
        description,
        badgeId,
        touched,
        folder
      })
    )
    await dispatch("loadCourses")
  },
  async deleteCourse({ commit, dispatch }, courseId) {
    commit(DELETE_COURSE, await CoursesAPI.delete(courseId))
    await dispatch("loadCourses")
  },
  async assignCourses({ commit, dispatch }, { userId, courseIds }) {
    commit(ASSIGN_COURSES, await UsersAPI.assignCourses(userId, courseIds))
    await dispatch("loadCourses")
  },
  async addLearningItemToCourse(
    { commit, dispatch },
    { courseId, learningItemId }
  ) {
    commit(ASSIGN_COURSES, await CoursesAPI.addItem(courseId, learningItemId))
    await dispatch("loadCourses")
  },

  async assignLearningItems(
    { commit, dispatch },
    { courseId, learningItemIds, newAsCopy }
  ) {
    commit(
      ASSIGN_LEARNING_ITEMS,
      await CoursesAPI.assignLearningItems(courseId, learningItemIds, newAsCopy)
    )
    await dispatch("loadCourses")
    await dispatch("learningItems/loadLearningItems", {}, { root: true })
  }
}

// mutations
const mutations = {
  [SET_COURSES](state, courses) {
    state.all = courses
  },
  [SET_COURSE](state, course) {
    let foundCourse = state.all.find(el => el.id === course.id)
    if (foundCourse) {
      foundCourse = course
    } else {
      state.all.push(course)
    }
  },
  [ADD_COURSE](state, course) {
    state.all.push(course)
  },
  [DELETE_COURSE](state, courseId) {
    let index = state.all.findIndex(el => el.id === courseId)
    state.all.splice(index, 1)
  },
  [ASSIGN_COURSES](state, courseId) {},
  [ASSIGN_LEARNING_ITEMS](state, course) {},
  [UPDATE_COURSE](state, course) {
    let courseToUpdate = state.all.find(el => course.id === el.id)
    courseToUpdate.name = course.name
    courseToUpdate.description = course.description
  },
  reset(state) {
    state.all.splice(0, state.all.length)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
